import { FC } from 'react';
import {
  Checkbox,
  Icon,
  SocialMediaIcon,
  SocialMediaIconProps,
  Tooltip,
} from '@kontentino/ui';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { TrashPost } from 'api/post';
import PageTypeUtils from 'app/utils/pageType';
import dayjs from 'dayjs';
import HorizontalPostCardImagePreviewWithPostTypeIcon from 'app/modules/posts/components/horizontalPostCard/HorizontalPostCardImagePreviewWithPostTypeIcon';
import HorizontalPostCardTags from 'app/modules/posts/components/horizontalPostCard/HorizontalPostCardTags';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';

type Props = {
  post: TrashPost;
  isSelected?: boolean;
  onToggleSelection?: () => void;
};

const TrashPostItem: FC<Props> = ({ post, isSelected, onToggleSelection }) => {
  const { t } = useTranslation();

  const postDateFormatted = post.date
    ? dayjs(`${post.date} ${post.time || '00:00'}`).format('D.MM.YYYY, HH:mm')
    : null;

  return (
    <div
      className={clsx(
        'tw-relative tw-mx-2 tw-flex tw-w-auto tw-cursor-pointer tw-gap-3 tw-rounded-lg tw-p-2 tw-transition-colors hover:tw-bg-grayscale-10',
        { 'tw-bg-primary-5': isSelected },
      )}
      data-post-id={post.id}
      data-cy="trash-post-item"
      onClick={onToggleSelection}
    >
      {onToggleSelection && (
        <div
          className="tw-flex tw-shrink-0 tw-items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={onToggleSelection}
          />
        </div>
      )}
      <div className="tw-shrink-0">
        <HorizontalPostCardImagePreviewWithPostTypeIcon
          image={{
            src: post.preview_image ?? undefined,
          }}
          postType={post.type}
          postSubtype={post.subtype}
        />
      </div>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-sm">
          {post.isGlobal && (
            <Tooltip content={t('globalPost', 'Global post')}>
              <Icon
                icon={faGlobe}
                className="tw-text-grayscale-100"
                size="sm"
              />
            </Tooltip>
          )}
          <SocialMediaIcon
            size={getYtLogoGuidelineSize(16, post.page.type)}
            type={
              PageTypeUtils.getName(
                post.page.type,
              ) as SocialMediaIconProps['type']
            }
            className="tw-shrink-0"
          />
          <span className="tw-max-w-[140px] tw-truncate tw-font-medium tw-text-grayscale-180">
            {post.page.name}
          </span>
        </div>
        {postDateFormatted && (
          <div className="tw-mb-1 tw-text-xs tw-text-grayscale-100">
            {postDateFormatted}
          </div>
        )}
        <div
          className={clsx('tw-line-clamp-2 tw-text-sm', {
            'tw-text-grayscale-100': !post.text,
          })}
        >
          {post.text || t('placeholder.noText')}
        </div>
        {post.tags.length > 0 && <HorizontalPostCardTags tags={post.tags} />}
      </div>
    </div>
  );
};

export default TrashPostItem;
